import type { FetchError, SalonStockerHttpTypes } from "@repo/api-sdk";
import sdk from "@repo/api-sdk";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";

import { favoritesQueryKeys } from "./queryKeys";

/**
 * Client data layer for **Favorites** (KAN-14) - a cheap `(seller_id,
 * variant_id)` membership set plus the single-tap toggle mutation. Favorites
 * is a `kind: "favorites"` saved list under the hood (api ADR-0002); once one
 * exists, the full CRUD/preview/add-to-cart surface is `queries/customer/
 * saved-lists.ts`, unmodified - this module owns only the two
 * favorites-specific entry points.
 */

export function favoritesQuery() {
  return queryOptions({
    queryKey: favoritesQueryKeys.all(),
    queryFn: () => sdk.store.favorites.list(),
    staleTime: 60 * 1000
  });
}

export function useFavorites(options?: { enabled?: boolean }) {
  return useQuery({ ...favoritesQuery(), ...options });
}

/** The id of the buyer's favorites list (`kind: "favorites"`), or `null` until one exists. */
export function useFavoritesListId(): string | null {
  return useFavorites().data?.list_id ?? null;
}

const favoriteKey = (item: SalonStockerHttpTypes.StoreFavoriteKey): string =>
  `${item.seller_id}:${item.variant_id}`;

/** Whether a `(seller_id, variant_id)` line is in the given favorites set. */
export function isFavorited(
  favorites: SalonStockerHttpTypes.StoreFavorites | undefined,
  item: SalonStockerHttpTypes.StoreFavoriteKey
): boolean {
  if (!favorites) return false;
  const target = favoriteKey(item);
  return favorites.items.some((favorite) => favoriteKey(favorite) === target);
}

/**
 * Flip membership of a `(seller_id, variant_id)` line, optimistically - the
 * heart icon fills/empties immediately, before the round trip resolves.
 * Rolls back to the pre-toggle set on error.
 */
export function useToggleFavorite() {
  const queryClient = useQueryClient();

  return useMutation<
    { favorited: boolean; list_id: string },
    FetchError,
    SalonStockerHttpTypes.StoreToggleFavorite,
    { previous?: SalonStockerHttpTypes.StoreFavorites }
  >({
    mutationFn: (item) => sdk.store.favorites.toggle(item),
    onMutate: async (item) => {
      await queryClient.cancelQueries({ queryKey: favoritesQueryKeys.all() });

      const previous =
        queryClient.getQueryData<SalonStockerHttpTypes.StoreFavorites>(
          favoritesQueryKeys.all()
        );

      const wasFavorited = isFavorited(previous, item);

      queryClient.setQueryData<SalonStockerHttpTypes.StoreFavorites>(
        favoritesQueryKeys.all(),
        (current) => {
          const base = current ?? { list_id: null, items: [] };
          return {
            list_id: base.list_id,
            items: wasFavorited
              ? base.items.filter(
                  (favorite) => favoriteKey(favorite) !== favoriteKey(item)
                )
              : [...base.items, item]
          };
        }
      );

      return { previous };
    },
    onError: (_err, _item, context) => {
      if (context?.previous) {
        queryClient.setQueryData(favoritesQueryKeys.all(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: favoritesQueryKeys.all() });
    }
  });
}
